import React from "react";

const STORAGE_KEY = "venpro.preferences.v1";

export const defaultPreferences = Object.freeze({
  theme: "dark",
  sidebarCollapsed: false,
  sidebarPinned: true,
  sidebarCollapseMode: "dynamic",
  sidebarBehavior: "inline",
  sidebarDisplayMode: "icon-and-text-expanded",
  navItemSize: "default",
  topbarVisible: false,
  topbarItemSize: "default",
  topbarDisplayMode: "icon-and-label",
  mobileAppbarVisible: true,
  mobileAppbarSize: "default",
  mobileAppbarItemsCount: 3,
  mobileAppbarDisplayMode: "icon-and-label",
  preferredName: "",
  moduleVisibility: {},
  navigationOrder: {
    sidebar: [],
    topbar: [],
    mobile: [],
  },
});

const layoutPreferenceKeys = [
  "theme",
  "sidebarCollapsed",
  "sidebarPinned",
  "sidebarCollapseMode",
  "sidebarBehavior",
  "sidebarDisplayMode",
  "navItemSize",
  "topbarVisible",
  "topbarItemSize",
  "topbarDisplayMode",
  "mobileAppbarVisible",
  "mobileAppbarSize",
  "mobileAppbarItemsCount",
  "mobileAppbarDisplayMode",
  "navigationOrder",
];

const AppPreferencesContext = React.createContext(null);

function readPreferences() {
  if (typeof window === "undefined") return defaultPreferences;
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    return {
      ...defaultPreferences,
      ...stored,
      sidebarCollapseMode: stored.sidebarCollapseMode
        ?? (stored.sidebarCollapsed ? "collapsed" : defaultPreferences.sidebarCollapseMode),
      sidebarBehavior: stored.sidebarBehavior
        ?? (stored.sidebarPinned === false ? "overlay" : defaultPreferences.sidebarBehavior),
      moduleVisibility: {
        ...defaultPreferences.moduleVisibility,
        ...(stored.moduleVisibility ?? {}),
      },
      navigationOrder: {
        ...defaultPreferences.navigationOrder,
        ...(stored.navigationOrder ?? {}),
      },
    };
  } catch {
    return defaultPreferences;
  }
}

function resolveTheme(theme) {
  if (theme !== "system" || typeof window === "undefined") return theme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function AppPreferencesProvider({ children }) {
  const [preferences, setPreferences] = React.useState(readPreferences);

  React.useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);

  React.useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const applyTheme = () => {
      document.documentElement.dataset.theme = resolveTheme(preferences.theme);
      document.documentElement.style.colorScheme = resolveTheme(preferences.theme);
    };
    applyTheme();
    media.addEventListener("change", applyTheme);
    return () => media.removeEventListener("change", applyTheme);
  }, [preferences.theme]);

  const setPreference = React.useCallback((key, value) => {
    setPreferences((current) => ({
      ...current,
      [key]: typeof value === "function" ? value(current[key]) : value,
    }));
  }, []);

  const setModuleVisible = React.useCallback((moduleId, visible) => {
    setPreferences((current) => ({
      ...current,
      moduleVisibility: {
        ...current.moduleVisibility,
        [moduleId]: visible,
      },
    }));
  }, []);

  const isModuleVisible = React.useCallback(
    (moduleId, defaultVisible = true) => preferences.moduleVisibility[moduleId] ?? defaultVisible,
    [preferences.moduleVisibility],
  );

  const resetPreferences = React.useCallback(() => {
    setPreferences({
      ...defaultPreferences,
      moduleVisibility: {},
      navigationOrder: { ...defaultPreferences.navigationOrder },
    });
  }, []);

  const resetLayoutPreferences = React.useCallback(() => {
    setPreferences((current) => {
      const next = { ...current };
      layoutPreferenceKeys.forEach((key) => {
        next[key] = defaultPreferences[key];
      });
      return next;
    });
  }, []);

  const value = React.useMemo(() => ({
    preferences,
    setPreference,
    setModuleVisible,
    isModuleVisible,
    resetLayoutPreferences,
    resetPreferences,
  }), [isModuleVisible, preferences, resetLayoutPreferences, resetPreferences, setModuleVisible, setPreference]);

  return (
    <AppPreferencesContext.Provider value={value}>
      {children}
    </AppPreferencesContext.Provider>
  );
}

export function useAppPreferences() {
  const context = React.useContext(AppPreferencesContext);
  if (!context) throw new Error("useAppPreferences must be used inside AppPreferencesProvider.");
  return context;
}
